import { useState, useCallback } from "react";

const EMPTY_PRODUCT = () => ({
	id: crypto.randomUUID(),
	productID: null,
	activeIngredientText: "",
	objectiveID: null,
	typeDetail: "y",
	dose100L: null,
	doseHa: null,
	observations: "",
	isReadOnly: false,
});

const EMPTY_GROUP = () => ({
	id: crypto.randomUUID(),
	subGroupKey: null,
	products: [EMPTY_PRODUCT()],
	alternatives: [],
});

const EMPTY_FORM = () => ({
	phytosanitaryProgramID: null,
	isNewProgram: false,
	newProgramName: "",
	newProgramEstateParentID: null,
	newProgramCropCustomerID: null,
	newProgramCropDetailID: null,
	newProgramOATypeCustomerID: null,
	applicationNumber: "",
	periodText: "",
	phenologicalStateID: null,
	phenologicalStateText: "",
	wetness: null,
	groups: [EMPTY_GROUP()],
});

export function usePhytosanitaryApplicationForm() {
	const [formState, setFormState] = useState(EMPTY_FORM());
	const [formErrors, setFormErrors] = useState({});

	const resetForm = useCallback(() => {
		setFormState(EMPTY_FORM());
		setFormErrors({});
	}, []);

	const prefillFromRow = useCallback((row, existingRows = []) => {
		const subGroupMap = {};
		existingRows.forEach((existingRow) => {
			const sg = existingRow.sub_group ?? "";
			if (!subGroupMap[sg]) {
				subGroupMap[sg] = { main: [], alt: [] };
			}
			const existingProduct = {
				id: crypto.randomUUID(),
				productID: existingRow.product_id ?? null,
				activeIngredientText: existingRow.active_ingredient_text || "",
				objectiveID: existingRow.objective_id ?? null,
				typeDetail: existingRow.type_detail || "y",
				dose100L: existingRow.dose_100l ?? null,
				doseHa: existingRow.dose_ha == null ? null : existingRow.dose_ha / 1000,
				observations: existingRow.observations || "",
				isReadOnly: true,
			};
			if (existingRow.is_subgroup_optional) {
				subGroupMap[sg].alt.push(existingProduct);
			} else {
				subGroupMap[sg].main.push(existingProduct);
			}
		});

		const groups = Object.keys(subGroupMap)
			.sort((a, b) => {
				const numA = Number.parseInt(a, 10);
				const numB = Number.parseInt(b, 10);
				if (Number.isFinite(numA) && Number.isFinite(numB)) return numA - numB;
				if (Number.isFinite(numA)) return -1;
				if (Number.isFinite(numB)) return 1;
				return a.localeCompare(b);
			})
			.map((sg) => ({
				id: crypto.randomUUID(),
				subGroupKey: sg,
				products: subGroupMap[sg].main,
				alternatives: subGroupMap[sg].alt,
			}));
		const hasOnlyUnGrouped = groups.length === 1 && groups[0].subGroupKey === "";
		if (hasOnlyUnGrouped) {
			groups[0] = { ...groups[0], products: [...groups[0].products, EMPTY_PRODUCT()] };
		} else {
			groups.push(EMPTY_GROUP());
		}

		setFormState({
			applicationNumber: row.application_number ? String(row.application_number) : "",
			periodText: row.period_text || "",
			phenologicalStateID: row.phenological_state_id ?? null,
			phenologicalStateText: row.phenological_state_text || "",
			wetness: row.wetness ?? null,
			groups,
		});
		setFormErrors({});
	}, []);

	const updateHeaderField = useCallback((field, value) => {
		setFormState((previous) => ({ ...previous, [field]: value }));
		setFormErrors((previous) => ({ ...previous, [field]: undefined }));
	}, []);

	const addGroup = useCallback(() => {
		setFormState((previous) => ({
			...previous,
			groups: [...previous.groups, EMPTY_GROUP()],
		}));
	}, []);

	const removeGroup = useCallback((groupId) => {
		setFormState((previous) => ({
			...previous,
			groups: previous.groups.filter((group) => group.id !== groupId),
		}));
	}, []);

	const addProductToGroup = useCallback((groupId) => {
		setFormState((previous) => ({
			...previous,
			groups: previous.groups.map((group) => (group.id === groupId ? { ...group, products: [...group.products, EMPTY_PRODUCT()] } : group)),
		}));
	}, []);

	const removeProductFromGroup = useCallback((groupId, productId) => {
		setFormState((previous) => ({
			...previous,
			groups: previous.groups.map((group) =>
				group.id === groupId ? { ...group, products: group.products.filter((product) => product.id !== productId) } : group,
			),
		}));
	}, []);

	const addAlternativeToGroup = useCallback((groupId) => {
		setFormState((previous) => ({
			...previous,
			groups: previous.groups.map((group) => (group.id === groupId ? { ...group, alternatives: [...group.alternatives, EMPTY_PRODUCT()] } : group)),
		}));
	}, []);

	const removeAlternativeFromGroup = useCallback((groupId, alternativeId) => {
		setFormState((previous) => ({
			...previous,
			groups: previous.groups.map((group) =>
				group.id === groupId ? { ...group, alternatives: group.alternatives.filter((alternative) => alternative.id !== alternativeId) } : group,
			),
		}));
	}, []);

	const updateProduct = useCallback((groupId, productId, field, value) => {
		setFormState((previous) => ({
			...previous,
			groups: previous.groups.map((group) =>
				group.id === groupId
					? {
							...group,
							products: group.products.map((product) => (product.id === productId ? { ...product, [field]: value } : product)),
						}
					: group,
			),
		}));
	}, []);

	const updateAlternative = useCallback((groupId, alternativeId, field, value) => {
		setFormState((previous) => ({
			...previous,
			groups: previous.groups.map((group) =>
				group.id === groupId
					? {
							...group,
							alternatives: group.alternatives.map((alternative) =>
								alternative.id === alternativeId ? { ...alternative, [field]: value } : alternative,
							),
						}
					: group,
			),
		}));
	}, []);

	const validate = useCallback(
		(mode) => {
			const errors = {};

			if (mode === "new_application") {
				if (formState.isNewProgram) {
					if (!formState.newProgramName || formState.newProgramName.trim() === "") {
						errors.newProgramName = "El nombre del programa es requerido";
					}
					if (!formState.newProgramEstateParentID) {
						errors.newProgramEstateParentID = "Debe seleccionar un campo";
					}
					if (!formState.newProgramCropCustomerID) {
						errors.newProgramCropCustomerID = "Debe seleccionar una especie";
					}
				} else if (!formState.phytosanitaryProgramID) {
					errors.phytosanitaryProgramID = "Debe seleccionar un programa";
				}
			}

			if (!formState.applicationNumber || String(formState.applicationNumber).trim() === "") {
				errors.applicationNumber = "El número de aplicación es requerido";
			}

			const hasAnyProduct = formState.groups.some(
				(group) =>
					group.products.some((product) => product.productID && !product.isReadOnly) ||
					group.alternatives.some((alternative) => alternative.productID && !alternative.isReadOnly),
			);

			if (!hasAnyProduct) {
				errors.groups = "Debe agregar al menos un producto nuevo";
			}

			setFormErrors(errors);
			return Object.keys(errors).length === 0;
		},
		[formState],
	);

	const mapToRows = useCallback(() => {
		const rows = [];

		const maxExistingSubGroup = formState.groups.reduce((max, group) => {
			if (group.subGroupKey !== null) {
				const num = Number.parseInt(group.subGroupKey, 10);
				return Number.isFinite(num) ? Math.max(max, num) : max;
			}
			return max;
		}, 0);
		let newGroupCounter = 0;

		formState.groups.forEach((group) => {
			let subGroup;
			if (group.subGroupKey !== null) {
				subGroup = group.subGroupKey;
			} else {
				newGroupCounter += 1;
				subGroup = String(maxExistingSubGroup + newGroupCounter);
			}

			const groupHasExistingProducts = group.products.some((product) => product.isReadOnly);
			const groupHasExistingAlternatives = group.alternatives.some((alternative) => alternative.isReadOnly);

			let newProductIndex = 0;
			group.products.forEach((product) => {
				if (product.isReadOnly || !product.productID) return;
				rows.push({
					sub_group: subGroup,
					is_main_product: newProductIndex === 0 && !groupHasExistingProducts,
					is_subgroup_optional: false,
					product_id: Number(product.productID),
					type_detail: product.typeDetail || "",
					objective_id: product.objectiveID ? Number(product.objectiveID) : undefined,
					dose_100l: product.dose100L === null ? undefined : Number(product.dose100L),
					dose_ha: product.doseHa === null ? undefined : Math.round(Number(product.doseHa) * 1000),
					observations: product.observations || "",
				});
				newProductIndex += 1;
			});

			let altSubGroup = null;
			let newAltIndex = 0;
			group.alternatives.forEach((alternative) => {
				if (alternative.isReadOnly || !alternative.productID) return;
				if (altSubGroup === null) {
					newGroupCounter += 1;
					altSubGroup = String(maxExistingSubGroup + newGroupCounter);
				}
				rows.push({
					sub_group: altSubGroup,
					is_main_product: newAltIndex === 0 && !groupHasExistingAlternatives,
					is_subgroup_optional: true,
					product_id: Number(alternative.productID),
					type_detail: alternative.typeDetail || "",
					objective_id: alternative.objectiveID ? Number(alternative.objectiveID) : undefined,
					dose_100l: alternative.dose100L === null ? undefined : Number(alternative.dose100L),
					dose_ha: alternative.doseHa === null ? undefined : Math.round(Number(alternative.doseHa) * 1000),
					observations: alternative.observations || "",
				});
				newAltIndex += 1;
			});
		});

		return rows;
	}, [formState]);

	return {
		formState,
		formErrors,
		resetForm,
		prefillFromRow,
		updateHeaderField,
		addGroup,
		removeGroup,
		addProductToGroup,
		removeProductFromGroup,
		addAlternativeToGroup,
		removeAlternativeFromGroup,
		updateProduct,
		updateAlternative,
		validate,
		mapToRows,
	};
}
